<script setup lang="ts">
import { ref, computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'

const router = useRouter()
const auth = useAuthStore()
const display = useDisplay()
const isMobile = computed(() => display.smAndDown.value)

// На десктопе — постоянный sidebar; на мобайле — bottom-nav.
const drawerOpen = ref(false)

interface NavItem {
  to: string
  icon: string
  title: string
  /** Видим только админу. */
  adminOnly?: boolean
}

const ALL_NAV: NavItem[] = [
  { to: '/', icon: 'mdi-view-dashboard-outline', title: 'Дашборд' },
  { to: '/leads', icon: 'mdi-account-multiple-outline', title: 'Заявки' },
  { to: '/content', icon: 'mdi-file-document-edit-outline', title: 'Контент' },
  { to: '/audit', icon: 'mdi-history', title: 'Аудит', adminOnly: true },
  { to: '/settings', icon: 'mdi-cog-outline', title: 'Настройки', adminOnly: true },
  { to: '/users', icon: 'mdi-account-cog-outline', title: 'Пользователи', adminOnly: true },
]

const isAdmin = computed(() => auth.role === 'admin')

const nav = computed(() => ALL_NAV.filter((i) => !i.adminOnly || isAdmin.value))

async function onLogout() {
  await auth.logout()
  router.push('/login')
}
</script>

<template>
  <v-app>
    <!-- Sidebar (desktop / md+) -->
    <v-navigation-drawer
      v-if="!isMobile"
      :model-value="true"
      permanent
      rail-width="72"
      :rail="false"
      width="240"
      color="surface"
      class="border-e border-opacity-25"
    >
      <div class="d-flex align-center px-4 py-4" style="gap:12px">
        <div
          class="d-flex align-center justify-center"
          style="width:36px;height:36px;background:#e11d48;border-radius:10px;font-weight:900;color:white"
        >
          A
        </div>
        <div>
          <div class="font-weight-bold">Admin</div>
          <div class="text-caption text-medium-emphasis">influence</div>
        </div>
      </div>
      <v-divider />
      <v-list density="comfortable" nav>
        <v-list-item
          v-for="item in nav"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          rounded="lg"
        />
      </v-list>
      <template #append>
        <div class="pa-3">
          <div class="text-caption text-medium-emphasis mb-1">
            {{ auth.user?.displayName }}
          </div>
          <div class="text-caption text-medium-emphasis mb-3">
            {{ auth.user?.email }}
          </div>
          <v-btn
            block
            variant="tonal"
            color="error"
            prepend-icon="mdi-logout"
            @click="onLogout"
          >
            Выйти
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Mobile app-bar -->
    <v-app-bar v-if="isMobile" :elevation="0" color="background" density="comfortable" class="border-b">
      <v-app-bar-title class="font-weight-bold">Admin</v-app-bar-title>
      <template #append>
        <v-menu>
          <template #activator="{ props }">
            <v-btn v-bind="props" icon="mdi-dots-vertical" />
          </template>
          <v-list>
            <v-list-item :subtitle="auth.user?.email" :title="auth.user?.displayName" />
            <v-divider />
            <v-list-item prepend-icon="mdi-logout" title="Выйти" @click="onLogout" />
          </v-list>
        </v-menu>
      </template>
    </v-app-bar>

    <v-main>
      <v-container fluid class="pa-4 pa-md-6" :class="{ 'pb-24': isMobile }">
        <slot />
      </v-container>
    </v-main>

    <!-- Bottom-nav (mobile only) -->
    <v-bottom-navigation
      v-if="isMobile"
      grow
      bg-color="surface"
      :elevation="6"
      style="padding-bottom: env(safe-area-inset-bottom)"
    >
      <v-btn
        v-for="item in nav"
        :key="item.to"
        :to="item.to"
        size="small"
      >
        <v-icon>{{ item.icon }}</v-icon>
        <span style="font-size:11px">{{ item.title }}</span>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>
